import React, { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import PropTypes from "prop-types";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
} from "@coreui/react";

const EmiCalculator = () => {
  const [loanAmount, setLoanAmount] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [tenure, setTenure] = useState("");
  const [frequency, setFrequency] = useState("Monthly");
  const [interestType, setInterestType] = useState("Reducing");
  const [monthlyEmi, setMonthlyEmi] = useState(null);
  const [totalPrincipal, setTotalPrincipal] = useState(null);
  const [totalInterest, setTotalInterest] = useState(null);
  const [totalAmount, setTotalAmount] = useState(null);
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);
  const [startDate, setStartDate] = useState("");
  //File Charges
  const [fileCharge, setFileCharge] = useState("");

  const calculateEMI = () => {
    const principal = parseFloat(loanAmount);
    const annualInterest = parseFloat(interestRate);
    const filecharge = parseFloat(fileCharge) || 0;
    let period = parseInt(tenure);

    if (!principal || !annualInterest || !period) return;

    let rate, frequencyLabel;
    let emiDate = new Date(startDate); // Initialize EMI start date

    switch (frequency) {
      case "Daily":
        rate = annualInterest / 100 / 365;
        frequencyLabel = "Day";
        break;
      case "Weekly":
        rate = annualInterest / 100 / 52;
        frequencyLabel = "Week";
        break;
      case "Monthly":
      default:
        rate = annualInterest / 100 / 12;
        frequencyLabel = "Month";
        break;
    }

    let emi = 0;
    let totalInt = 0;
    const schedule = [];
    const effectivePrincipal = principal + filecharge;

    if (interestType === "Reducing") {
      emi =
        (effectivePrincipal * rate * Math.pow(1 + rate, period)) /
        (Math.pow(1 + rate, period) - 1);
      let remainingBalance = effectivePrincipal;

      for (let i = 1; i <= period; i++) {
        const interestPayment = remainingBalance * rate;
        const principalPayment = emi - interestPayment;
        remainingBalance -= principalPayment;

        totalInt += interestPayment;

        // Add EMI date to schedule
        const formattedDate = formatDate(emiDate);
        schedule.push({
          month: i,
          emi: emi.toFixed(2),
          principalPayment: principalPayment.toFixed(2),
          interestPayment: interestPayment.toFixed(2),
          remainingBalance:
            remainingBalance > 0 ? remainingBalance.toFixed(2) : "0.00",
          emiDate: formattedDate, // EMI date
        });

        // Increment date based on frequency
        emiDate = incrementDate(emiDate, frequency);
      }
    } else if (interestType === "Flat") {
      const totalInterestAmount =
        (effectivePrincipal * annualInterest * (period / getPeriodDivisor())) / 100;
      const totalPayment = effectivePrincipal + totalInterestAmount;
      emi = totalPayment / period;
      totalInt = totalInterestAmount;

      for (let i = 1; i <= period; i++) {
        const principalPayment = effectivePrincipal / period;
        const interestPayment = totalInterestAmount / period;
        const remainingBalance = effectivePrincipal - principalPayment * i;

        // Add EMI date to schedule
        const formattedDate = formatDate(emiDate);
        schedule.push({
          month: i,
          emi: emi.toFixed(2),
          principalPayment: principalPayment.toFixed(2),
          interestPayment: interestPayment.toFixed(2),
          remainingBalance:
            remainingBalance > 0 ? remainingBalance.toFixed(2) : "0.00",
          emiDate: formattedDate, // EMI date
        });

        // Increment date based on frequency
        emiDate = incrementDate(emiDate, frequency);
      }
    }

    const totalPayable = principal + totalInt;

    setMonthlyEmi(emi.toFixed(2));
    setTotalPrincipal(principal.toFixed(2));
    setTotalInterest(totalInt.toFixed(2));
    setTotalAmount(totalPayable.toFixed(2));
    setAmortizationSchedule(schedule);
  };

  const getPeriodDivisor = () => {
    switch (frequency) {
      case "Daily":
        return 365;
      case "Weekly":
        return 52;
      default:
        return 12;
    }
  };

  const incrementDate = (date, frequency) => {
    let newDate = new Date(date);
    switch (frequency) {
      case "Daily":
        newDate.setDate(newDate.getDate() + 1); // Increment by 1 day
        break;
      case "Weekly":
        newDate.setDate(newDate.getDate() + 7); // Increment by 1 week
        break;
      case "Monthly":
      default:
        newDate.setMonth(newDate.getMonth() + 1); // Increment by 1 month
        break;
    }
    return newDate;
  };

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const tableRef = useRef();
  const [printState, setPrintState] = useState(false);
  const generatePDF = useReactToPrint({
    content: () => tableRef.current,
    documentTitle: "EMI_Schedule",
    onAfterPrint: () => setPrintState(false),
  });

  useEffect(() => {
    if (printState) generatePDF();
  }, [printState]);

  const download_btn = () => {
    setPrintState(true);
  };

  const TableComponent = ({
    monthlyemi,
    totalPri,
    totalInt,
    totalAmountPay,
    fileChargeAmt,
  }) => (
    <div
      style={{
        background: "#fff",
        borderRadius: "10px",
        boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
      }}
      className={printState ? "p-3" : "p-0"}
    >
      <div style={{ padding: "30px" }}>
        <h2>Results</h2>
        <p>EMI: ₹{monthlyemi}</p>
        <p>Total Principal: ₹{totalPri}</p>
        <p>File Charge: ₹{fileChargeAmt}</p>
        <p>Total Interest: ₹{totalInt}</p>
        <p>Total Amount Payable: ₹{totalAmountPay}</p>
        <h3 className="mt-4">Amortization Schedule</h3>
        <div className="table-responsive">
          <table className="table table-striped table-bordered">
            <thead>
              <tr>
                <th>#</th>
                <th>EMI</th>
                <th>Principal</th>
                <th>Interest</th>
                <th>Balance</th>
                <th>EMI Date</th> {/* Add EMI Date column */}
              </tr>
            </thead>
            <tbody>
              {amortizationSchedule.map((row) => (
                <tr key={row.month}>
                  <td>{row.month}</td>
                  <td>₹{row.emi}</td>
                  <td>₹{row.principalPayment}</td>
                  <td>₹{row.interestPayment}</td>
                  <td>₹{row.remainingBalance}</td>
                  <td>{row.emiDate}</td> {/* Display EMI Date */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  TableComponent.propTypes = {
    monthlyemi: PropTypes.string.isRequired,
    totalPri: PropTypes.string.isRequired,
    totalInt: PropTypes.string.isRequired,
    totalAmountPay: PropTypes.string.isRequired,
    fileChargeAmt: PropTypes.string.isRequired,
  };

  return (
    <CRow
      className="content"
      style={{
        padding: "20px",
        marginTop: "20px",
        borderRadius: "10px",
      }}
    >
      <CCol xs={12}>
        <CCard className="mb-4 custom-card">
          <CCardHeader className="custom-card-header">
            <h5>EMI Calculator</h5>
          </CCardHeader>
          <CRow>
            <CCol xs={12}>
              <CCardBody>
                <CCard>
                  <CCardBody>
                    <div className="container-fluid">
                      <div className="col-lg-12">
                        <div className="">
                          <div
                            style={{
                              background:
                                "linear-gradient(135deg, #1e2639, #222b40, #4c5a7a, #7c90c2)",
                              borderRadius: "10px",
                            }}
                            className="card-body"
                          >
                            <div className="p-3">
                              <div className="input-group mb-3">
                                <span className="input-group-text border-0">
                                  ₹
                                </span>
                                <input
                                  type="number"
                                  id="loanAmount"
                                  className="form-control"
                                  placeholder="Enter Loan Amount"
                                  onChange={(e) =>
                                    setLoanAmount(e.target.value)
                                  }
                                />
                                <span className="input-group-text border-0">
                                  .00
                                </span>
                              </div>
                              <div className="input-group mb-3">
                                <span className="input-group-text border-0">
                                  ₹
                                </span>
                                <input
                                  type="number"
                                  id="fileCharge"
                                  className="form-control"
                                  placeholder="Enter File Charge"
                                  value={fileCharge}
                                  onChange={(e) => setFileCharge(e.target.value)}
                                />
                                <span className="input-group-text border-0">
                                  .00
                                </span>
                              </div>

                              <div className="mb-3">
                                <label htmlFor="interestRate">
                                  Interest Rate (%):
                                </label>
                                <input
                                  type="number"
                                  className="form-control"
                                  id="interestRate"
                                  value={interestRate}
                                  onChange={(e) =>
                                    setInterestRate(e.target.value)
                                  }
                                  placeholder="Enter Interest Rate"
                                  required
                                />
                              </div>

                              <div className="mb-3">
                                <label htmlFor="tenure">Tenure:</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  id="tenure"
                                  value={tenure}
                                  onChange={(e) => setTenure(e.target.value)}
                                  placeholder={`Enter Tenure in ${frequency}`}
                                  required
                                />
                              </div>

                              <div className="mb-3">
                                <label>Start Date:</label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={startDate}
                                  onChange={(e) => setStartDate(e.target.value)}
                                  required
                                />
                              </div>

                              <div className="mb-3">
                                <label>Payment Frequency:</label>
                                <select
                                  className="form-control"
                                  value={frequency}
                                  onChange={(e) => setFrequency(e.target.value)}
                                >
                                  <option value="Daily">Daily</option>
                                  <option value="Weekly">Weekly</option>
                                  <option value="Monthly">Monthly</option>
                                </select>
                              </div>

                              <div className="mb-3">
                                <label>Interest Type:</label>
                                <select
                                  className="form-control"
                                  value={interestType}
                                  onChange={(e) =>
                                    setInterestType(e.target.value)
                                  }
                                >
                                  <option value="Reducing">Reducing</option>
                                  <option value="Flat">Flat</option>
                                </select>
                              </div>

                              <button
                                className="btn btn-success"
                                onClick={calculateEMI}
                              >
                                Calculate EMI
                              </button>

                              {monthlyEmi && (
                                <div className="mt-4">
                                  <TableComponent
                                    monthlyemi={monthlyEmi}
                                    totalPri={totalPrincipal}
                                    totalInt={totalInterest}
                                    totalAmountPay={totalAmount}
                                    fileChargeAmt={(parseFloat(fileCharge) || 0).toFixed(2)}
                                  />

                                  <div style={{ display: "none" }}>
                                    <div ref={tableRef}>
                                      <div
                                        style={{
                                          width: "100%",
                                          display: "flex",
                                          justifyContent: "space-between",
                                          background:
                                            "linear-gradient(180deg, #1e2639, #222b40, #4c5a7a, #7c90c2)",
                                          padding: "20px",
                                        }}
                                      >
                                        <p style={{ color: "#FFF" }}>
                                          EMI Calculator
                                        </p>
                                        <div style={{ textAlign: "start" }}>
                                          <p
                                            style={{
                                              fontWeight: "bold",
                                              color: "#FFF",
                                              fontSize: "20px",
                                            }}
                                          ></p>
                                        </div>
                                      </div>
                                      <TableComponent
                                        monthlyemi={monthlyEmi}
                                        totalPri={totalPrincipal}
                                        totalInt={totalInterest}
                                        totalAmountPay={totalAmount}
                                        fileChargeAmt={(parseFloat(fileCharge) || 0).toFixed(2)}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CCardBody>
                </CCard>
              </CCardBody>
            </CCol>
          </CRow>
        </CCard>
      </CCol>
    </CRow>
  );
};

export default EmiCalculator;
